// Roofing Industries homeowner catalogue. Product geometry is presentation-only:
// changing a profile never changes the measured roof packet or any quantity.

import type { RoofAggregates } from "./contract";

export type ProfileId =
  | "corrugate"
  | "true_oak_corrugate"
  | "true_oak_deep"
  | "trimrib"
  | "ribline_960"
  | "multirib"
  | "rt7"
  | "eurostyle_spanlok"
  | "eurostyle_epic"
  | "eurostyle_eurolok"
  | "multidek"
  | "ri925";

export type ProfilePattern = "sinusoidal" | "trapezoidal" | "standing_seam";
export type PriceTier = "reference" | "mid" | "premium";
export type ProfileFamilyId = "classic" | "ribbed" | "architectural";
export type CatalogueTier = "featured" | "extended" | "consultation";

export interface PublishedProfileDimensions {
  pitchMM?: number;
  ribHeightMM?: number;
  effectiveCoverMM?: number;
  sheetWidthMM?: number;
  trayWidthsMM?: number[];
  trayWidthRangeMM?: [number, number];
}

export interface Profile {
  id: ProfileId;
  name: string;
  family: string;
  familyId: ProfileFamilyId;
  catalogueTier: CatalogueTier;
  character: string;
  summary: string;
  reasons: string[];
  sourceUrl: string;
  sourceLabel: string;
  dimensions: PublishedProfileDimensions;
  pattern: ProfilePattern;
  texturePeriodM: number;
  bumpScale: number;
  installedLook: {
    sheetDirection: string;
    fixing: string;
    ridgeAndHip: string;
  };
  priceTier: PriceTier;
  /** Prototype-only sample rate. Final commercial rates remain upstream in BOQ. */
  sampleRatePerM2?: number;
  supportedColourIds: string[];
  availabilityNote?: string;
  presentationNote?: string;
}

export interface Colour {
  id: string;
  name: string;
  hex: number;
}

export const COLOURS: Colour[] = [
  { id: "ironsand", name: "Ironsand", hex: 0x2e3033 },
  { id: "ebony", name: "Ebony", hex: 0x1b1b1d },
  { id: "grey_friars", name: "Grey Friars", hex: 0x54585a },
  { id: "windsor_grey", name: "Windsor Grey", hex: 0x66686a },
  { id: "titania", name: "Titania", hex: 0xd8d5cc },
  { id: "sandstone_grey", name: "Sandstone Grey", hex: 0x9c9b95 },
  { id: "scoria", name: "Scoria", hex: 0x6e3b34 },
  { id: "pioneer_red", name: "Pioneer Red", hex: 0x7c4a3a },
  { id: "karaka", name: "Karaka", hex: 0x2c302b },
  { id: "permanent_green", name: "Permanent Green", hex: 0x2f4a3a },
  { id: "new_denim_blue", name: "New Denim Blue", hex: 0x3e4e5e },
];

// Each RI product page lists COLORSTEEL® / ColorCote® finishes. Every name below is in the
// manufacturers' published colour ranges; actual branch stock and environmental grade are
// still confirmed physically. Keeping the matrix explicit prevents accidental catalogue drift.
const COMMON_COLOURS = COLOURS.map((colour) => colour.id);
export const PROFILE_COLOUR_COMPATIBILITY: Record<ProfileId, string[]> = {
  corrugate: [...COMMON_COLOURS],
  true_oak_corrugate: [...COMMON_COLOURS],
  true_oak_deep: [...COMMON_COLOURS],
  trimrib: [...COMMON_COLOURS],
  ribline_960: [...COMMON_COLOURS],
  multirib: [...COMMON_COLOURS],
  rt7: [...COMMON_COLOURS],
  eurostyle_spanlok: [...COMMON_COLOURS],
  eurostyle_epic: [...COMMON_COLOURS],
  eurostyle_eurolok: [...COMMON_COLOURS],
  multidek: [...COMMON_COLOURS],
  ri925: [...COMMON_COLOURS],
};

export const COLOUR_RANGE_SOURCES = [
  "https://www.colorsteel.co.nz/products/colours/",
  "https://www.colorcote.co.nz/colour-palette/",
] as const;

export const PROFILES: Profile[] = [
  {
    id: "corrugate",
    name: "Corrugate",
    family: "Traditional corrugate",
    familyId: "classic",
    catalogueTier: "extended",
    character: "Light, familiar and timeless",
    summary: "Roofing Industries' traditional 76.2 mm corrugated profile creates a fine, regular rhythm across the roof.",
    reasons: [
      "The shallow 19 mm wave creates a familiar New Zealand roofline.",
      "Its finer rhythm appears lighter than the deeper True Oak profiles.",
      "A straightforward traditional comparison for residential roofs.",
    ],
    sourceUrl: "https://www.roof.co.nz/product/corrugate",
    sourceLabel: "Roofing Industries Corrugate profile technical summary",
    dimensions: { pitchMM: 76.2, ribHeightMM: 19, effectiveCoverMM: 762, sheetWidthMM: 845 },
    pattern: "sinusoidal",
    texturePeriodM: 0.0762,
    bumpScale: 0.026,
    installedLook: {
      sheetDirection: "Continuous corrugated sheets follow the fall to the gutter",
      fixing: "Exposed fasteners repeat in an aligned roof-wide pattern",
      ridgeAndHip: "Profiled closures sit beneath broad folded cappings",
    },
    priceTier: "reference",
    supportedColourIds: PROFILE_COLOUR_COMPATIBILITY.corrugate,
  },
  {
    id: "true_oak_corrugate",
    name: "True Oak® Corrugate",
    family: "Classic corrugate",
    familyId: "classic",
    catalogueTier: "featured",
    character: "Familiar, rounded and calm",
    summary: "A deeper, rounder corrugate that gives a traditional roofline more definition without making it feel busy.",
    reasons: [
      "Rounded lines sit calmly across this hip-and-valley roof.",
      "The reference price point makes comparison straightforward.",
      "A balanced starting point when no style preference has been supplied.",
    ],
    sourceUrl: "https://www.roof.co.nz/product/true-oak-corrugate",
    sourceLabel: "Roofing Industries True Oak® Corrugate technical literature",
    dimensions: { pitchMM: 76.2, ribHeightMM: 21.3, effectiveCoverMM: 762, sheetWidthMM: 835 },
    pattern: "sinusoidal",
    texturePeriodM: 0.0762,
    bumpScale: 0.032,
    installedLook: {
      sheetDirection: "Continuous runs from ridge or hip towards the gutter",
      fixing: "Exposed fasteners form a regular, aligned rhythm",
      ridgeAndHip: "Broad folded cappings finish the rounded profile",
    },
    priceTier: "reference",
    sampleRatePerM2: 165,
    supportedColourIds: PROFILE_COLOUR_COMPATIBILITY.true_oak_corrugate,
  },
  {
    id: "true_oak_deep",
    name: "True Oak® Deep",
    family: "Bold corrugate",
    familyId: "classic",
    catalogueTier: "featured",
    character: "Deeper curves and stronger shadow",
    summary: "A much larger corrugated form for homeowners who want the roof itself to read as a strong design feature.",
    reasons: [
      "The 40.3 mm rib creates a bolder rhythm in changing light.",
      "Its wider wave reads clearly from the street and from the garden.",
      "It keeps a corrugated character while making a stronger statement.",
    ],
    sourceUrl: "https://www.roof.co.nz/product/true-oak-deep",
    sourceLabel: "Roofing Industries True Oak® Deep technical literature",
    dimensions: { pitchMM: 152.5, ribHeightMM: 40.3, effectiveCoverMM: 915, sheetWidthMM: 1040 },
    pattern: "sinusoidal",
    texturePeriodM: 0.1525,
    bumpScale: 0.052,
    installedLook: {
      sheetDirection: "Wide waves follow each roof plane down to the gutter",
      fixing: "Exposed fasteners align with the deeper corrugate rhythm",
      ridgeAndHip: "Broad folded cappings close the deeper profile",
    },
    priceTier: "premium",
    sampleRatePerM2: 178,
    supportedColourIds: PROFILE_COLOUR_COMPATIBILITY.true_oak_deep,
  },
  {
    id: "trimrib",
    name: "Trimrib®",
    family: "Low-pitch trapezoidal",
    familyId: "ribbed",
    catalogueTier: "extended",
    character: "Straight, efficient and quietly modern",
    summary: "A 32 mm trapezoidal profile with broad pans and straight shadow lines, available in several RI swage treatments.",
    reasons: [
      "The broad pans give each roof plane a calm linear appearance.",
      "Its sharper ribs create more definition than corrugate.",
      "The representative visual uses the standard single-swage presentation.",
    ],
    sourceUrl: "https://www.roof.co.nz/product/trimrib",
    sourceLabel: "Roofing Industries Trimrib® profile technical summary",
    dimensions: { pitchMM: 190, ribHeightMM: 32, effectiveCoverMM: 760, sheetWidthMM: 820 },
    pattern: "trapezoidal",
    texturePeriodM: 0.19,
    bumpScale: 0.04,
    installedLook: {
      sheetDirection: "Broad pans and ribs run directly with the roof fall",
      fixing: "Exposed fixings align beside the regular ribs",
      ridgeAndHip: "Notched folded cappings close the trapezoidal profile",
    },
    priceTier: "mid",
    supportedColourIds: PROFILE_COLOUR_COMPATIBILITY.trimrib,
    availabilityNote: "S, TS and V swage variants vary by RI branch; final selection is confirmed locally.",
    presentationNote: "Shown as the standard Trimrib S visual.",
  },
  {
    id: "ribline_960",
    name: "Ribline® 960",
    family: "Wide-cover trapezoidal",
    familyId: "ribbed",
    catalogueTier: "extended",
    character: "Broad, orderly and efficient",
    summary: "A 960 mm effective-cover profile whose six-rib rhythm creates long, even lines with fewer sheet laps.",
    reasons: [
      "The wide sheet cover reduces the number of visible side laps.",
      "Its medium rib reads clearly without dominating the roof.",
      "The capillary lapping edge is an RI performance detail; final suitability remains upstream.",
    ],
    sourceUrl: "https://www.roof.co.nz/product/ribline-r-800",
    sourceLabel: "Roofing Industries Ribline® 960 April 2026 literature",
    dimensions: { pitchMM: 160, ribHeightMM: 32, effectiveCoverMM: 960, sheetWidthMM: 1030 },
    pattern: "trapezoidal",
    texturePeriodM: 0.16,
    bumpScale: 0.04,
    installedLook: {
      sheetDirection: "Six evenly spaced ribs follow the roof fall",
      fixing: "Exposed pan fixings create a disciplined grid",
      ridgeAndHip: "Notched cappings follow the medium-height rib",
    },
    priceTier: "mid",
    supportedColourIds: PROFILE_COLOUR_COMPATIBILITY.ribline_960,
    availabilityNote: "Manufactured in Auckland for North Island applications; confirm local supply with RI.",
  },
  {
    id: "multirib",
    name: "Multirib®",
    family: "High-performance trapezoidal",
    familyId: "ribbed",
    catalogueTier: "extended",
    character: "Strong, functional and deeply ribbed",
    summary: "A 43 mm multi-purpose profile with broad pans and pronounced ribs, used across residential and larger roof forms.",
    reasons: [
      "The 43 mm rib produces strong, practical shadow lines.",
      "Its wide 910 mm cover keeps the roof visually ordered.",
      "It offers a robust alternative to the finer RT7 rhythm.",
    ],
    sourceUrl: "https://www.roof.co.nz/product/multirib",
    sourceLabel: "Roofing Industries Multirib® October 2024 literature",
    dimensions: { pitchMM: 182, ribHeightMM: 43, effectiveCoverMM: 910, sheetWidthMM: 1005 },
    pattern: "trapezoidal",
    texturePeriodM: 0.182,
    bumpScale: 0.048,
    installedLook: {
      sheetDirection: "High ribs and broad pans run continuously down each plane",
      fixing: "Exposed pan fixings align beside every principal rib",
      ridgeAndHip: "Deep notched cappings close the 43 mm profile",
    },
    priceTier: "mid",
    supportedColourIds: PROFILE_COLOUR_COMPATIBILITY.multirib,
    availabilityNote: "Branch manufacturing and final residential suitability are confirmed by RI.",
  },
  {
    id: "rt7",
    name: "RT7®",
    family: "Trapezoidal rib",
    familyId: "ribbed",
    catalogueTier: "featured",
    character: "Crisp, practical and contemporary",
    summary: "A medium-height trapezoidal profile with regular ribs and a clean, engineered appearance.",
    reasons: [
      "Straight ribs give the six roof surfaces a clean visual order.",
      "The sharper profile creates a contemporary alternative to corrugate.",
      "Its sample price sits between the two corrugated options.",
    ],
    sourceUrl: "https://www.roof.co.nz/product/rt7",
    sourceLabel: "Roofing Industries RT7® profile technical summary",
    dimensions: { pitchMM: 127, ribHeightMM: 43, effectiveCoverMM: 889, sheetWidthMM: 965 },
    pattern: "trapezoidal",
    texturePeriodM: 0.127,
    bumpScale: 0.046,
    installedLook: {
      sheetDirection: "Straight trapezoidal ribs run with the roof fall",
      fixing: "Exposed fasteners align on the regular rib centres",
      ridgeAndHip: "Notched folded cappings create a crisp junction",
    },
    priceTier: "mid",
    sampleRatePerM2: 172,
    supportedColourIds: PROFILE_COLOUR_COMPATIBILITY.rt7,
  },
  {
    id: "eurostyle_spanlok",
    name: "Eurostyle® Spanlok®",
    family: "Standing seam",
    familyId: "architectural",
    catalogueTier: "featured",
    character: "Architectural, precise and premium",
    summary: "A standing-seam tray profile with tall, widely spaced ribs that produces long, confident shadow lines.",
    reasons: [
      "The 45 mm seams create a distinctly architectural roofscape.",
      "Wide trays make each roof surface feel clean and deliberate.",
      "It is the premium visual alternative; final pricing must be checked.",
    ],
    sourceUrl: "https://www.roof.co.nz/product/eurostyle-spanlok",
    sourceLabel: "Roofing Industries Eurostyle® Spanlok® technical literature",
    dimensions: { ribHeightMM: 45, trayWidthsMM: [370, 450] },
    pattern: "standing_seam",
    texturePeriodM: 0.45,
    bumpScale: 0.058,
    installedLook: {
      sheetDirection: "Wide trays and 45 mm seams run cleanly with the fall",
      fixing: "Concealed clips keep the tray surface visually uninterrupted",
      ridgeAndHip: "Folded standing-seam cappings preserve the architectural line",
    },
    priceTier: "premium",
    supportedColourIds: PROFILE_COLOUR_COMPATIBILITY.eurostyle_spanlok,
  },
  {
    id: "eurostyle_epic",
    name: "Eurostyle® Epic™",
    family: "Custom architectural seam",
    familyId: "architectural",
    catalogueTier: "consultation",
    character: "Bespoke, refined and highly architectural",
    summary: "Epic is a configurable RI tray system spanning angle seam, double standing seam, roll seam, roll cap and Snaplok expressions.",
    reasons: [
      "The family offers the broadest choice of architectural seam character.",
      "Pan widths are selected for the project rather than treated as one fixed sheet.",
      "The roof shows a representative double-standing-seam visual for comparison.",
    ],
    sourceUrl: "https://www.roof.co.nz/product/eurostyle-epic",
    sourceLabel: "Roofing Industries Eurostyle® 2024 technical brochure",
    dimensions: { ribHeightMM: 38, trayWidthRangeMM: [195, 515] },
    pattern: "standing_seam",
    texturePeriodM: 0.45,
    bumpScale: 0.052,
    installedLook: {
      sheetDirection: "Custom trays run in long lines with the roof fall",
      fixing: "The representative double-seam visual keeps fixings concealed",
      ridgeAndHip: "Project-specific seamed cappings finish the selected Epic system",
    },
    priceTier: "premium",
    supportedColourIds: PROFILE_COLOUR_COMPATIBILITY.eurostyle_epic,
    availabilityNote: "Exact Epic seam, pan width, substrate and supply require RI architectural confirmation.",
    presentationNote: "Representative double-standing-seam visual; Epic is a configurable product family.",
  },
  {
    id: "eurostyle_eurolok",
    name: "Eurostyle® Eurolok™",
    family: "50 mm standing seam",
    familyId: "architectural",
    catalogueTier: "featured",
    character: "Tall, crisp and strongly architectural",
    summary: "A 50 mm standing-seam profile with broad trays and pronounced shadow lines for a distinctly architectural finish.",
    reasons: [
      "The 50 mm seam is the tallest architectural rib in the homeowner shelf.",
      "Broad 365 mm or 450 mm trays keep large roof planes composed.",
      "It provides a more emphatic standing-seam alternative to Spanlok.",
    ],
    sourceUrl: "https://www.roof.co.nz/product/eurostyle-eurolok",
    sourceLabel: "Roofing Industries Eurostyle® 2024 technical brochure",
    dimensions: { ribHeightMM: 50, trayWidthsMM: [365, 450] },
    pattern: "standing_seam",
    texturePeriodM: 0.45,
    bumpScale: 0.062,
    installedLook: {
      sheetDirection: "Wide trays and tall seams run continuously with the fall",
      fixing: "Concealed clips leave the broad pans uninterrupted",
      ridgeAndHip: "Folded architectural cappings preserve the 50 mm seam rhythm",
    },
    priceTier: "premium",
    supportedColourIds: PROFILE_COLOUR_COMPATIBILITY.eurostyle_eurolok,
    availabilityNote: "Profile, substrate and regional availability are confirmed by RI.",
  },
  {
    id: "multidek",
    name: "Multidek®",
    family: "Concealed-clip rib",
    familyId: "architectural",
    catalogueTier: "featured",
    character: "Bold, wide and mechanically clean",
    summary: "A 500 mm concealed-clip profile with a 50 mm rib and broad pans, creating a strong architectural roof with no exposed roof fasteners.",
    reasons: [
      "The concealed clip leaves the roof surface visually clean.",
      "A 50 mm rib creates bold shadow lines without a narrow tray rhythm.",
      "The 500 mm cover gives it a distinct look beside standing seam.",
    ],
    sourceUrl: "https://www.roof.co.nz/product/multidek",
    sourceLabel: "Roofing Industries Multidek® 2020 product literature",
    dimensions: { pitchMM: 500, ribHeightMM: 50, effectiveCoverMM: 500, sheetWidthMM: 520 },
    pattern: "trapezoidal",
    texturePeriodM: 0.5,
    bumpScale: 0.058,
    installedLook: {
      sheetDirection: "Wide concealed-clip panels follow the roof fall",
      fixing: "Concealed clips remove exposed fasteners from the main roof surface",
      ridgeAndHip: "Deep folded cappings close the broad 50 mm profile",
    },
    priceTier: "premium",
    supportedColourIds: PROFILE_COLOUR_COMPATIBILITY.multidek,
    availabilityNote: "Manufacturing and supply are region-dependent; confirm with RI.",
  },
  {
    id: "ri925",
    name: "RI925™",
    family: "Wide-cover trapezoidal",
    familyId: "ribbed",
    catalogueTier: "extended",
    character: "Wide, strong and highly structured",
    summary: "An RI-exclusive 925 mm wide-cover profile with medium-height ribs for residential, rural and commercial roof forms.",
    reasons: [
      "Five principal ribs create a strong but uncluttered rhythm.",
      "The wide cover reduces the number of sheet laps across the roof.",
      "Its steeper rib faces give more definition than a shallow trapezoidal profile.",
    ],
    sourceUrl: "https://www.roof.co.nz/product/ri925",
    sourceLabel: "Roofing Industries RI925™ 2025 product literature",
    dimensions: { pitchMM: 185, ribHeightMM: 41, effectiveCoverMM: 925, sheetWidthMM: 981 },
    pattern: "trapezoidal",
    texturePeriodM: 0.185,
    bumpScale: 0.046,
    installedLook: {
      sheetDirection: "Five wide-cover ribs follow the fall of each roof plane",
      fixing: "Exposed pan fixings align in a regular structural grid",
      ridgeAndHip: "Notched folded cappings finish the medium-height ribs",
    },
    priceTier: "mid",
    supportedColourIds: PROFILE_COLOUR_COMPATIBILITY.ri925,
    availabilityNote: "Manufactured in Tauranga for North Island supply; confirm local availability with RI.",
  },
];

export const DEFAULT_PROFILE_ID: ProfileId = "true_oak_corrugate";
export const DEFAULT_COLOUR_ID = "ironsand";

export function findProfile(id: string | undefined): Profile | undefined {
  return PROFILES.find((profile) => profile.id === id);
}

export function findColour(id: string | undefined): Colour | undefined {
  return COLOURS.find((colour) => colour.id === id);
}

export function coloursForProfile(profile: Profile): Colour[] {
  return COLOURS.filter((colour) => profile.supportedColourIds.includes(colour.id));
}

export type EstimateInput = Pick<RoofAggregates, "surfaceAreaM2" | "storeys">;

export interface EstimateGuide {
  mid: number;
  low: number;
  high: number;
}

const ESTIMATE = { fixed: 6500, storeyAdder: 2500, bandLow: 0.8, bandHigh: 1.25 } as const;

export function calculateEstimateGuide(input: EstimateInput, profile: Profile): EstimateGuide | null {
  if (profile.sampleRatePerM2 === undefined) return null;
  const mid = profile.sampleRatePerM2 * input.surfaceAreaM2 + ESTIMATE.fixed + (input.storeys > 1 ? ESTIMATE.storeyAdder : 0);
  return { mid, low: mid * ESTIMATE.bandLow, high: mid * ESTIMATE.bandHigh };
}

export interface RelativePrice {
  kind: "same" | "more" | "less" | "unknown";
  amount?: number;
  percentage?: number;
  label: string;
}

export function calculateRelativePrice(input: EstimateInput, profile: Profile, reference: Profile): RelativePrice {
  if (profile.sampleRatePerM2 === undefined || reference.sampleRatePerM2 === undefined) {
    const tier = profile.priceTier === "premium" ? "Premium" : profile.priceTier === "mid" ? "Mid-range" : "Standard";
    return { kind: "unknown", label: `${tier} — pricing to be checked` };
  }
  const delta = (profile.sampleRatePerM2 - reference.sampleRatePerM2) * input.surfaceAreaM2;
  if (Math.abs(delta) < 1) return { kind: "same", amount: 0, percentage: 0, label: "Reference price" };
  const rounded = Math.round(Math.abs(delta) / 500) * 500;
  const percentage = Math.round((Math.abs(profile.sampleRatePerM2 - reference.sampleRatePerM2) / reference.sampleRatePerM2) * 100);
  return {
    kind: delta > 0 ? "more" : "less",
    amount: Math.abs(delta),
    percentage,
    label: `About ${formatNZD(rounded)} ${delta > 0 ? "more" : "less"} · ${delta > 0 ? "+" : "−"}${percentage}% material`,
  };
}

export function formatNZD(value: number): string {
  return `NZ$${Math.round(value).toLocaleString("en-NZ")}`;
}

export function formatGuideValue(value: number): string {
  return formatNZD(Math.round(value / 500) * 500);
}
